import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { SectionTitle, SectionSubtitle } from '../../components/ui/Layout';
import { Table, TableWrap } from '../../components/ui/Table';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Field, Label, Input, Textarea, Select } from '../../components/ui/FormControls';
import { Spinner, ErrorMessage } from '../../components/ui/Feedback';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Block = styled.section`
  margin-bottom: 3rem;
`;

const emptyCategory = { id: null, nombre: '', orden: 0 };
const emptyItem = {
  id: null,
  categoria_id: '',
  nombre: '',
  descripcion: '',
  precio: '',
  disponible: true,
  orden: 0
};

function AdminMenu() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryForm, setCategoryForm] = useState(null);
  const [itemForm, setItemForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/admin/menu/categories', { token }),
      api.get('/api/admin/menu/items', { token })
    ])
      .then(([cats, its]) => {
        setCategories(cats);
        setItems(its);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, [token]);

  const categoryName = (id) => categories.find((c) => c.id === id)?.nombre || '—';

  const handleCategorySave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (categoryForm.id) {
        await api.put(`/api/admin/menu/categories/${categoryForm.id}`, categoryForm, { token });
      } else {
        await api.post('/api/admin/menu/categories', categoryForm, { token });
      }
      setCategoryForm(null);
      loadAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryDelete = async (cat) => {
    if (!window.confirm(`¿Eliminar la categoría "${cat.nombre}" y todos sus items?`)) return;
    try {
      await api.delete(`/api/admin/menu/categories/${cat.id}`, { token });
      loadAll();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleItemSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...itemForm, precio: Number(itemForm.precio) };
      if (itemForm.id) {
        await api.put(`/api/admin/menu/items/${itemForm.id}`, payload, { token });
      } else {
        await api.post('/api/admin/menu/items', payload, { token });
      }
      setItemForm(null);
      loadAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleItemDelete = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.nombre}"?`)) return;
    try {
      await api.delete(`/api/admin/menu/items/${item.id}`, { token });
      loadAll();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <Spinner $size="28px" />;
  }

  return (
    <div>
      <SectionTitle $align="left">Menú</SectionTitle>
      <SectionSubtitle $align="left">Administra categorías y platillos/bebidas.</SectionSubtitle>
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <Block>
        <Toolbar>
          <h3 style={{ margin: 0 }}>Categorías</h3>
          <Button onClick={() => setCategoryForm(emptyCategory)}>+ Nueva categoría</Button>
        </Toolbar>

        {categoryForm && (
          <Card as="form" onSubmit={handleCategorySave} style={{ marginBottom: '1.5rem' }}>
            <Field>
              <Label htmlFor="cat-nombre">Nombre</Label>
              <Input
                id="cat-nombre"
                required
                value={categoryForm.nombre}
                onChange={(e) => setCategoryForm((f) => ({ ...f, nombre: e.target.value }))}
              />
            </Field>
            <Field>
              <Label htmlFor="cat-orden">Orden</Label>
              <Input
                id="cat-orden"
                type="number"
                value={categoryForm.orden}
                onChange={(e) => setCategoryForm((f) => ({ ...f, orden: e.target.value }))}
              />
            </Field>
            <Actions>
              <Button type="submit" disabled={saving}>
                {saving ? <Spinner $size="18px" /> : 'Guardar'}
              </Button>
              <Button type="button" $variant="ghost" onClick={() => setCategoryForm(null)}>
                Cancelar
              </Button>
            </Actions>
          </Card>
        )}

        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Orden</th>
                <th>Nombre</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat.id}>
                  <td>{cat.orden}</td>
                  <td>{cat.nombre}</td>
                  <td>
                    <Actions>
                      <Button $variant="ghost" onClick={() => setCategoryForm(cat)}>
                        Editar
                      </Button>
                      <Button $variant="danger" onClick={() => handleCategoryDelete(cat)}>
                        Eliminar
                      </Button>
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </Block>

      <Block>
        <Toolbar>
          <h3 style={{ margin: 0 }}>Items del Menú</h3>
          <Button onClick={() => setItemForm(emptyItem)}>+ Nuevo item</Button>
        </Toolbar>

        {itemForm && (
          <Card as="form" onSubmit={handleItemSave} style={{ marginBottom: '1.5rem' }}>
            <Field>
              <Label htmlFor="item-categoria">Categoría</Label>
              <Select
                id="item-categoria"
                required
                value={itemForm.categoria_id}
                onChange={(e) => setItemForm((f) => ({ ...f, categoria_id: e.target.value }))}
              >
                <option value="">Selecciona una categoría</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nombre}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label htmlFor="item-nombre">Nombre</Label>
              <Input
                id="item-nombre"
                required
                value={itemForm.nombre}
                onChange={(e) => setItemForm((f) => ({ ...f, nombre: e.target.value }))}
              />
            </Field>
            <Field>
              <Label htmlFor="item-descripcion">Descripción</Label>
              <Textarea
                id="item-descripcion"
                value={itemForm.descripcion || ''}
                onChange={(e) => setItemForm((f) => ({ ...f, descripcion: e.target.value }))}
              />
            </Field>
            <Field>
              <Label htmlFor="item-precio">Precio</Label>
              <Input
                id="item-precio"
                required
                type="number"
                step="0.01"
                min="0"
                value={itemForm.precio}
                onChange={(e) => setItemForm((f) => ({ ...f, precio: e.target.value }))}
              />
            </Field>
            <Field>
              <Label htmlFor="item-orden">Orden</Label>
              <Input
                id="item-orden"
                type="number"
                value={itemForm.orden}
                onChange={(e) => setItemForm((f) => ({ ...f, orden: e.target.value }))}
              />
            </Field>
            <Field>
              <Label htmlFor="item-disponible">
                <input
                  id="item-disponible"
                  type="checkbox"
                  checked={Boolean(itemForm.disponible)}
                  onChange={(e) => setItemForm((f) => ({ ...f, disponible: e.target.checked }))}
                  style={{ marginRight: '0.5rem' }}
                />
                Disponible
              </Label>
            </Field>
            <Actions>
              <Button type="submit" disabled={saving}>
                {saving ? <Spinner $size="18px" /> : 'Guardar'}
              </Button>
              <Button type="button" $variant="ghost" onClick={() => setItemForm(null)}>
                Cancelar
              </Button>
            </Actions>
          </Card>
        )}

        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Categoría</th>
                <th>Nombre</th>
                <th>Precio</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{categoryName(item.categoria_id)}</td>
                  <td>{item.nombre}</td>
                  <td>${Number(item.precio).toFixed(2)}</td>
                  <td>
                    <Badge $status={item.disponible ? 'confirmada' : 'cancelada'}>
                      {item.disponible ? 'Disponible' : 'Agotado'}
                    </Badge>
                  </td>
                  <td>
                    <Actions>
                      <Button $variant="ghost" onClick={() => setItemForm(item)}>
                        Editar
                      </Button>
                      <Button $variant="danger" onClick={() => handleItemDelete(item)}>
                        Eliminar
                      </Button>
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </Block>
    </div>
  );
}

export default AdminMenu;
